import {
  type ActionCreatorWithPayload,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import { F_KEY_NM, FILTERS_CONFIG } from '../../../shared/constants';
import {
  type ServiceIdentityActionManagerState,
  type ServiceIdentityFilters,
  type ServiceIdentityItem,
  type ServiceIdentitySortField,
  type ServiceIdentitySortOrder,
} from '../../../shared/types';
import {
  type ServiceIdentityState,
  ServiceIdentityType,
} from '../data-layer/serviceIdentityType';

const initialState: ServiceIdentityState = {
  // General
  isGlobalLoading: false,
  // Search
  searchText: '',
  // Filters
  isFilterBar: false,
  filters: FILTERS_CONFIG,
  activeFilters: {},
  // Sort
  sort: F_KEY_NM,
  order: 'asc',
  // List
  list: {
    isListLoading: false,
    totalCount: null,
    rowsToUpdate: null,
  },
  // Sidepanel
  sidePanel: {
    isSidePanelOpen: false,
    sidePanelData: null,
  },
  // Actions
  actionManager: null,
};

const serviceIdentitySlice = createSlice({
  name: ServiceIdentityType.SLICE_NAME,
  initialState,
  reducers: {
    // General
    setIsGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.isGlobalLoading = action.payload;
    },
    // Search
    setSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    // Filters
    setIsFilterBarOpen: (state, action: PayloadAction<boolean>) => {
      state.isFilterBar = action.payload;
    },
    setFilters: (state, action: PayloadAction<ServiceIdentityFilters[]>) => {
      state.filters = action.payload;
    },
    setActiveFilters: (
      state,
      action: PayloadAction<
        Record<string, (string | number | Record<string, number | string>)[]>
      >
    ) => {
      state.activeFilters = action.payload;
    },
    // Sort
    setSort: (
      state,
      action: PayloadAction<{
        sort: ServiceIdentitySortField;
        order: ServiceIdentitySortOrder;
      }>
    ) => {
      state.sort = action.payload.sort;
      state.order = action.payload.order;
    },
    // List
    setListTotalCount: (state, action: PayloadAction<number | null>) => {
      state.list.totalCount = action.payload;
    },
    setIsListLoading: (state, action: PayloadAction<boolean>) => {
      state.list.isListLoading = action.payload;
    },
    setRowsToUpdate: (
      state,
      action: PayloadAction<Record<number, Partial<ServiceIdentityItem>> | null>
    ) => {
      state.list.rowsToUpdate = action.payload;
    },
    // Side Panel
    setIsSidePanelOpen: (state, action: PayloadAction<boolean>) => {
      state.sidePanel.isSidePanelOpen = action.payload;
    },
    setSidePanelData: (
      state,
      action: PayloadAction<ServiceIdentityItem | null>
    ) => {
      state.sidePanel.sidePanelData = action.payload;
    },
    // Actions
    setActionManager: (
      state,
      action: PayloadAction<ServiceIdentityActionManagerState | null>
    ) => {
      state.actionManager = action.payload;
    },
  },
});

const { reducer: serviceIdentityReducer, actions } = serviceIdentitySlice;

interface ServiceIdentityActions {
  setIsGlobalLoading: ActionCreatorWithPayload<boolean>;
  setSearchText: ActionCreatorWithPayload<string>;
  setIsFilterBarOpen: ActionCreatorWithPayload<boolean>;
  setFilters: ActionCreatorWithPayload<ServiceIdentityFilters[]>;
  setActiveFilters: ActionCreatorWithPayload<
    Record<string, (string | number | Record<string, number | string>)[]>
  >;
  setSort: ActionCreatorWithPayload<{
    sort: ServiceIdentitySortField;
    order: ServiceIdentitySortOrder;
  }>;
  setListTotalCount: ActionCreatorWithPayload<number | null>;
  setIsListLoading: ActionCreatorWithPayload<boolean>;
  setRowsToUpdate: ActionCreatorWithPayload<Record<
    number,
    Partial<ServiceIdentityItem>
  > | null>;
  setIsSidePanelOpen: ActionCreatorWithPayload<boolean>;
  setSidePanelData: ActionCreatorWithPayload<ServiceIdentityItem | null>;
  setActionManager: ActionCreatorWithPayload<ServiceIdentityActionManagerState | null>;
}

export const serviceIdentityActions = actions as ServiceIdentityActions;

/**
 * Reducer for the ServiceIdentity slice.
 * @returns The updated ServiceIdentityState.
 */
export default serviceIdentityReducer;
